import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface AiThought {
    /** Stable id — pair tool-call/tool-result events to update `ok`. */
    id: string;
    /** Human-readable label, e.g. "Querying violations…". */
    label: string;
    /** Undefined while in-flight; true on success, false on failure. */
    ok?: boolean;
    /**
     * 'tool' — a structural tool-call row (status icon + label).
     * 'summary' — a layperson-safe paraphrase of the model's reasoning,
     *             rendered as italic prose between/above tool rows.
     * Default 'tool' when omitted so existing callsites keep working.
     */
    kind?: 'tool' | 'summary';
}

/**
 * Renders a "Thought for Xs" timeline of tool-call rows, matching the
 * Figma frame at PayHOAI 1389:20396. Each row shows a state icon + a
 * vertical connector bar (omitted on the last row) + the thought text.
 *
 * The header doubles as a collapse toggle via the caret on the right.
 *
 * 👋 Heads-up on icons: this component uses the Phosphor webfont
 * (`<i class="ph-bold ph-…">`) instead of the V2 SVG sprite. That's
 * intentional — we're piloting Phosphor here alongside the AI assistant
 * page and approval dialog to evaluate it against the sprite pattern.
 * Reviews suggesting "use the sprite" can be deferred until after that
 * comparison lands.
 */
@Component({
    selector: 'app-ai-thought-trail',
    templateUrl: './ai-thought-trail.component.html',
    styleUrls: ['./ai-thought-trail.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
})
export class AiThoughtTrailComponent {
    @Input() thoughts: ReadonlyArray<AiThought> = [];
    /** Streamed extended-thinking prose, accumulated from thinking-delta events. */
    @Input() thinkingContent: string | null = null;
    /** True while the assistant turn is still streaming. Drives the header label. */
    @Input() streaming = false;
    /** Final elapsed seconds — only shown once `streaming` flips to false. */
    @Input() elapsedSeconds: number | null = null;
    @Input() collapsed = false;

    @Output() collapsedChange = new EventEmitter<boolean>();

    /**
     * The raw extended-thinking prose from the model is internal reasoning
     * about tool names, params, and system-prompt instructions — always
     * too technical to show users. Return empty so only the structured
     * tool-call rows (from `thoughts`) are displayed.
     */
    get thinkingRows(): string[] {
        return [];
    }

    onToggle(): void {
        // Don't mutate `this.collapsed` locally — emit the new value and let
        // the parent's two-way binding flow it back. Avoids the input/output
        // double-write and keeps state ownership in the parent.
        this.collapsedChange.emit(!this.collapsed);
    }

    trackById(_idx: number, t: AiThought): string {
        return t.id;
    }

    trackByIndex(idx: number): number {
        return idx;
    }
}
